/** Shared micro-noise tile (128×128 SVG fractal noise). */
const NOISE_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128">' +
    '<filter id="n"><feTurbulence type="fractalNoise" baseFrequency="0.88" numOctaves="4" stitchTiles="stitch"/></filter>' +
    '<rect width="100%" height="100%" filter="url(#n)"/></svg>';

export const BOARD_MAT_NOISE_DATA_URI = `data:image/svg+xml,${encodeURIComponent(NOISE_SVG)}`;

/**
 * @typedef {'matte' | 'felt' | 'cardboard' | 'paper' | 'wood'} BoardMatSurfaceKind
 */

/** @type {Record<BoardMatSurfaceKind, { noiseOpacity: number, blendMode: string, vignette: (hex: string) => string }>} */
const SURFACE_PROFILES = {
    matte: {
        noiseOpacity: 0.032,
        blendMode: "overlay",
        vignette: () =>
            "radial-gradient(ellipse 115% 90% at 50% 42%, rgba(255,255,255,0.045) 0%, transparent 58%)," +
            "radial-gradient(ellipse 100% 75% at 50% 100%, rgba(0,0,0,0.07) 0%, transparent 52%)",
    },
    felt: {
        noiseOpacity: 0.038,
        blendMode: "multiply",
        vignette: () =>
            "radial-gradient(ellipse 130% 95% at 50% 50%, rgba(0,0,0,0) 35%, rgba(0,0,0,0.12) 100%)," +
            "linear-gradient(165deg, rgba(255,255,255,0.03) 0%, transparent 42%, rgba(0,0,0,0.06) 100%)",
    },
    cardboard: {
        noiseOpacity: 0.04,
        blendMode: "multiply",
        vignette: () =>
            "linear-gradient(178deg, rgba(255,248,235,0.08) 0%, transparent 38%, rgba(90,60,35,0.09) 100%)," +
            "radial-gradient(circle at 18% 22%, rgba(255,255,255,0.06) 0%, transparent 45%)",
    },
    paper: {
        noiseOpacity: 0.028,
        blendMode: "overlay",
        vignette: () =>
            "radial-gradient(ellipse 120% 85% at 50% 35%, rgba(255,255,255,0.12) 0%, transparent 55%)," +
            "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.03) 100%)",
    },
    wood: {
        noiseOpacity: 0.034,
        blendMode: "multiply",
        vignette: () =>
            "linear-gradient(168deg, rgba(255,220,180,0.05) 0%, transparent 40%, rgba(20,8,4,0.14) 100%)," +
            "repeating-linear-gradient(92deg, rgba(255,255,255,0.012) 0 2px, rgba(0,0,0,0.018) 2px 4px)",
    },
};

/**
 * Layered board surface: base tint + soft vignette + micro-noise grain.
 *
 * @param {{ hex: string, surface?: BoardMatSurfaceKind }} preset
 * @returns {{ baseSx: object, textureSx: object }}
 */
export function getBoardMatSurfaceLayers(preset) {
    const surface = preset.surface ?? "matte";
    const profile = SURFACE_PROFILES[surface] ?? SURFACE_PROFILES.matte;

    return {
        baseSx: {
            backgroundColor: preset.hex,
            backgroundImage: profile.vignette(preset.hex),
            transition: "background-color 0.28s ease",
        },
        textureSx: {
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            pointerEvents: "none",
            zIndex: 0,
            opacity: profile.noiseOpacity,
            mixBlendMode: profile.blendMode,
            backgroundImage: `url("${BOARD_MAT_NOISE_DATA_URI}")`,
            backgroundSize: "128px 128px",
        },
    };
}

/** Compact texture preview for toolbar swatches. */
export function getBoardMatSwatchTextureSx(preset) {
    const { textureSx } = getBoardMatSurfaceLayers(preset);
    return {
        position: "absolute",
        inset: 0,
        borderRadius: "inherit",
        pointerEvents: "none",
        opacity: textureSx.opacity,
        mixBlendMode: textureSx.mixBlendMode,
        backgroundImage: textureSx.backgroundImage,
        backgroundSize: textureSx.backgroundSize,
    };
}
